import axAPI from 'httpConfig'
import { ShiftOptions } from 'models/ICattlesData'
import {
	IMilker,
	IMilkingScheduleAssignment,
	IMilkingParlorNamesResponse,
	IMilkingParlorZonesResponse,
	IMilkingShift,
	IMilkingSchedulesResponse,
	IMilkingProcess,
	IMilkingProcessDeviationEntry,
	IMilkingProcessDeviationResponse,
	IMilkingProcessGraph,
	IMilkingScheduleResponse,
	MilkingProcessDeviationType,
} from 'models/MilkingProcess'

export default class MilkingProcessService {
	static getMilkingProcessStats = async (
		startDate: number,
		endDate: number,
		parlorId: number[] | 'all',
		shift?: ShiftOptions[] | 'all',
		camera_id?: number | 'all',
	): Promise<IMilkingProcess> => {
		const params = new URLSearchParams()
		params.append('start', String(startDate))
		params.append('end', String(endDate))
		if (camera_id && camera_id !== 'all') {
			params.append('camera_id', String(camera_id))
		}
		if (camera_id && camera_id !== 'all') {
			params.append('camera_id', String(camera_id))
		}
		if (parlorId && parlorId !== 'all') {
			parlorId.forEach(id => {
				params.append('milking_parlor', String(id))
			})
		}
		if (shift && shift !== 'all') {
			shift.forEach(id => {
				params.append('period', String(id))
			})
		}
		const response = await axAPI.get(
			`milking/deviations/stats?${params.toString()}`,
		)
		try {
			return response.data
		} catch (e) {
			throw e
		}
	}

	static getMilkingProcessGraph = async (
		startDate: number,
		endDate: number,
		shiftPeriod: ShiftOptions[] | 'all',
		parlorId?: number[] | 'all',
		dayDate?: string,
	): Promise<IMilkingProcessGraph> => {
		const params = new URLSearchParams()

		if (dayDate) {
			// Новый эндпоинт для дневного графика (yyyy-mm-dd)
			if (shiftPeriod && shiftPeriod !== 'all') {
				shiftPeriod.forEach(shift => {
					params.append('period', String(shift))
				})
			}
			if (parlorId && parlorId !== 'all') {
				parlorId.forEach(id => {
					params.append('milking_parlor', String(id))
				})
			}
		} else {
			// Старый эндпоинт для диапазона дат
			params.append('start', String(startDate))
			params.append('end', String(endDate))
			if (shiftPeriod && shiftPeriod !== 'all') {
				shiftPeriod.forEach(shift => {
					params.append('period', String(shift))
				})
			}
			if (parlorId && parlorId !== 'all') {
				parlorId.forEach(id => {
					params.append('milking_parlor', String(id))
				})
			}
		}

		const url = dayDate
			? `milking/deviations/graph/${dayDate}?${params.toString()}`
			: `milking/deviations/graph?${params.toString()}`

		const response = await axAPI.get(url)
		try {
			return response.data
		} catch (e) {
			throw e
		}
	}
	static getMilkingProcessDeviations = async (
		startDate: number,
		endDate: number,
		shiftPeriod: ShiftOptions[] | 'all',
		page: number,
		pageSize: number,
		parlorId: number[] | 'all',
		milkerId?: number[] | 'all',
		deviationType?: MilkingProcessDeviationType[] | 'all',
		ordering?: string[] | null,
	): Promise<IMilkingProcessDeviationResponse> => {
		const params = new URLSearchParams()
		startDate && params.append('start', String(startDate))
		endDate && params.append('end', String(endDate))
		if (shiftPeriod && shiftPeriod !== 'all') {
			shiftPeriod.forEach(shift => {
				params.append('period', String(shift))
			})
		}
		page && params.append('page', String(page))
		pageSize && params.append('page_size', String(pageSize))
		if (parlorId && parlorId !== 'all') {
			parlorId.forEach(id => {
				params.append('milking_parlor', String(id))
			})
		}
		if (milkerId && milkerId !== 'all') {
			milkerId.forEach(id => {
				params.append('milker_id', String(id))
			})
		}
		if (deviationType && deviationType !== 'all') {
			deviationType.forEach(type => {
				params.append('type', String(type))
			})
		}
		if (ordering && ordering.length > 0) {
			ordering.forEach(order => {
				params.append('ordering', order)
			})
		}

		const response = await axAPI.get(`milking/deviations?${params.toString()}`)
		try {
			return response.data
		} catch (e) {
			throw e
		}
	}
	static updateMilkingProcessDeviationFalseMark = async (
		id: number,
		isFalse: boolean,
	): Promise<
		Pick<IMilkingProcessDeviationEntry, 'id' | 'is_false' | 'is_false_by'>
	> => {
		const response = await axAPI.patch(`milking/deviations/${id}`, {
			is_false: isFalse,
		})
		return response.data
	}
	static exportMilkingProcessDeviations = async (
		startDate: number,
		endDate: number,
		shiftPeriod: ShiftOptions[] | 'all',
		parlorId: number[] | 'all',
		milkerId?: number[] | 'all',
		deviationType?: MilkingProcessDeviationType[] | 'all',
		ordering?: string[] | null,
	) => {
		const params = new URLSearchParams()
		startDate && params.append('start', String(startDate))
		endDate && params.append('end', String(endDate))
		if (shiftPeriod && shiftPeriod !== 'all') {
			shiftPeriod.forEach(shift => {
				params.append('period', String(shift))
			})
		}
		if (parlorId && parlorId !== 'all') {
			parlorId.forEach(id => {
				params.append('milking_parlor', String(id))
			})
		}
		if (milkerId && milkerId !== 'all') {
			milkerId.forEach(id => {
				params.append('milker_id', String(id))
			})
		}
		if (deviationType && deviationType !== 'all') {
			deviationType.forEach(type => {
				params.append('type', String(type))
			})
		}
		if (ordering && ordering.length > 0) {
			ordering.forEach(order => {
				params.append('ordering', order)
			})
		}

		return await axAPI.get(`milking/deviations/export?${params.toString()}`, {
			responseType: 'arraybuffer',
		})
	}

	static getMilkingSchedule = async () => {
		const response = await axAPI.get<IMilkingScheduleResponse[]>(
			'/settings/milking_schedule',
		)
		return response.data
	}
	static updateMilkingSchedule = async (data: IMilkingScheduleResponse) => {
		const response = await axAPI.put('/settings/milking_schedule', data)
		return response.data
	}
	static getMilkingParlorNames =
		async (): Promise<IMilkingParlorNamesResponse> => {
			const response = await axAPI.get<IMilkingParlorNamesResponse>(
				'/milking/milking-parlor-names',
			)
			return response.data
		}

	static getParlorZones = async (
		parlorId: number,
	): Promise<IMilkingParlorZonesResponse> => {
		const response = await axAPI.get<IMilkingParlorZonesResponse>(
			`/milking/parlor/${parlorId}/zones`,
		)
		return response.data
	}

	static getMilkingShifts = async (): Promise<IMilkingShift[]> => {
		const response = await axAPI.get<IMilkingShift[]>('/milking/milking-shifts')
		return response.data
	}

	static getMilkersInfo = async (): Promise<IMilker[]> => {
		const response = await axAPI.get<IMilker[]>('/milking/milkers')
		return response.data
	}

	static getMilkingSchedules = async (props: {
		parlorId?: number
		milkerId?: number
		workDate?: string
		start?: number
		end?: number
	}): Promise<{
		items: IMilkingScheduleAssignment[]
		availablePeriods: ShiftOptions[]
	}> => {
		const queryParams = new URLSearchParams()
		if (props.parlorId) queryParams.append('parlor_id', String(props.parlorId))
		if (props.milkerId) queryParams.append('milker_id', String(props.milkerId))
		if (props.workDate) queryParams.append('work_date', props.workDate)
		if (props.start) queryParams.append('start', String(props.start))
		if (props.end) queryParams.append('end', String(props.end))

		const queryString = queryParams.toString()
		const response = await axAPI.get<
			IMilkingScheduleAssignment[] | IMilkingSchedulesResponse
		>(`/milking/milking-schedules${queryString ? `?${queryString}` : ''}`)

		if (Array.isArray(response.data)) {
			return { items: response.data, availablePeriods: [] }
		}

		return {
			items: response.data.items ?? [],
			availablePeriods: response.data.available_periods ?? [],
		}
	}

	static createMilkingSchedule = async (data: {
		milker: number
		parlor: number
		work_date: string
		shift_period: ShiftOptions
		zones: number[]
	}) => {
		const response = await axAPI.post<IMilkingScheduleAssignment>(
			'/milking/milking-schedules',
			data,
		)
		return response.data
	}

	static deleteMilkingSchedule = async (id: number | string): Promise<void> => {
		try {
			await axAPI.delete(`/milking/schedule/${id}`)
		} catch (_error) {
			await axAPI.delete(`/milking/milking-schedules/${id}`)
		}
	}

	static createMilker = async (
		fullName: string,
		photo: File | null,
	): Promise<IMilker> => {
		const formData = new FormData()
		formData.append('full_name', fullName)
		if (photo) formData.append('photo', photo)
		const response = await axAPI.post<IMilker>('/milking/milkers', formData)
		return response.data
	}

	static updateMilker = async (
		id: number | string,
		fullName: string,
		photo?: File | null,
	): Promise<IMilker> => {
		if (photo instanceof File) {
			const formData = new FormData()
			formData.append('full_name', fullName)
			formData.append('photo', photo)
			const response = await axAPI.patch<IMilker>(
				`/milking/milkers/${id}`,
				formData,
			)
			return response.data
		}

		const response = await axAPI.patch<IMilker>(`/milking/milkers/${id}`, {
			full_name: fullName,
			...(photo === null ? { photo: null } : {}),
		})
		return response.data
	}

	static deleteMilker = async (id: number | string): Promise<void> => {
		await axAPI.delete(`/milking/milkers/${id}`)
	}
}
